import React, { Fragment, useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { Button, LoadingSpinner, ConfirmationModal, AnnouncementFormModal } from '../components'
import { API_BASE_URL, LoadingSpinnerMeta, ConfirmationModalMeta, AnnouncementsFormModalMeta } from '../data'
import { PlusIcon, ChevronLeftIcon, DeleteIcon, EyeIcon } from '../icons'
import { Announcement, AnnouncementMetaProps } from '../data/types'
import { useResourcePermissions } from '../hooks/useResourcePermissions'

type Props = {
    meta: AnnouncementMetaProps,
}

export const Announcements = (props: Props) => {
    const { meta } = props
    const { canEditAnnouncement, canDeleteAnnouncement, canCreateAnnouncement } = useResourcePermissions()
    const [loading, setLoading] = useState(true)
    const [announcements, setAnnouncements] = useState<Announcement[]>([])
    const [error, setError] = useState<string | null>(null)
    const [isAnnouncementModalOpen, setIsAnnouncementModalOpen] = useState(false)
    const [selectedAnnouncement, setSelectedAnnouncement] = useState<Announcement | null>(null)
    const [isDeleteAnnouncementModalOpen, setIsDeleteAnnouncementModalOpen] = useState(false)
    const [isDeletingDuty, setIsDeletingDuty] = useState(false)
    const [filterText, setFilterText] = useState<string | null>(null)
    const [startDate, setStartDate] = useState<string | null>(null);
    const [endDate, setEndDate] = useState<string | null>(null);

    const [page, setPage] = useState(1)
    const pageSize = 6


    useEffect(() => {
        const fetchAnnouncements = async () => {
            setLoading(true)
            setError(null)

            try {
                const response = await fetch(`${API_BASE_URL}/announcement`, { credentials: 'include' })
                if (!response.ok) {
                    const text = await response.text()
                    const textObj = JSON.parse(text)
                    throw new Error(`${textObj.message}`)
                }
                const result = await response.json()
                setAnnouncements(result)
            }
            catch (err) {
                setError(err instanceof Error ? err.message : 'An error occurred while fetching duties')
            }
            finally {
                setLoading(false)
            }
        }

        fetchAnnouncements()

    }, [])

    const handleCreateAnnouncement = async (announcementData: any) => {
        const response = await fetch(`${API_BASE_URL}/announcement`,
            {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json'
                },
                credentials: 'include',
                body: JSON.stringify(announcementData)
            })

        if (!response.ok) {
            const text = await response.text()
            const textObj = JSON.parse(text)
            throw new Error(`${textObj.message}`)
        }

        const result = await response.json()

        if (result.model) {
            setAnnouncements(prevDuties => [...prevDuties, result.model])
        }
    }

    const handleUpdateAnnouncement = async (announcementData: Announcement) => {
        if (!selectedAnnouncement) return

        const response = await fetch(`${API_BASE_URL}/announcement/${selectedAnnouncement.id}`, {
            method: 'PUT',
            headers: {
                'Content-Type': 'application/json',
                'Accept': 'application/json'
            },
            credentials: 'include',
            body: JSON.stringify(announcementData)
        })

        if (!response.ok) {
            const text = await response.text()
            const errorText = JSON.parse(text)
            throw new Error(errorText.message);
        }

        const result = await response.json()

        if (result.model) {
            const updatedDuty = result.model
            setAnnouncements(prev =>
                prev.map(duty =>
                    duty.id === updatedDuty.id ? updatedDuty : duty
                )
            )
        }

    }

    const handleDeleteAnnouncementConfirm = async () => {
        if (!selectedAnnouncement) return

        setIsDeletingDuty(true)
        try {
            const response = await fetch(`${API_BASE_URL}/announcement/${selectedAnnouncement.id}`, {
                method: 'DELETE',
                headers: {
                    'Accept': 'application/json'
                },
                credentials: 'include'
            })

            if (!response.ok) {
                const text = await response.text()
                const errorText = JSON.parse(text)
                throw new Error(errorText.message);
            }

            setAnnouncements(prev => prev.filter(duty => duty.id !== selectedAnnouncement.id));
            setIsDeleteAnnouncementModalOpen(false)
            setSelectedAnnouncement(null)
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to delete Duty')
            setIsDeleteAnnouncementModalOpen(false)
        } finally {
            setIsDeletingDuty(false)
        }
    }

    const handleEditAnnouncementClick = (announcement: Announcement) => {
        setSelectedAnnouncement(announcement)
        setIsAnnouncementModalOpen(true)
    }

    const handleCreateAnnouncementClick = () => {
        setSelectedAnnouncement(null)
        setIsAnnouncementModalOpen(true)
    }


    const handleDeleteAnnouncementClick = (duty: Announcement) => {
        setSelectedAnnouncement(duty)
        setIsDeleteAnnouncementModalOpen(true)
    }

    const handleClose = () => {
        setSelectedAnnouncement(null)
        setIsAnnouncementModalOpen(false)
        setIsDeleteAnnouncementModalOpen(false)
    }

    const filterAnnouncements = announcements.filter((a) => {
        // Text filter
        const matchesText =
            !filterText ||
            a.title.toLowerCase().includes(filterText.toLowerCase());

        // Date conversion
        const announcementDate = new Date(a.updated_at);

        // Start date filter
        const matchesStartDate = !startDate || announcementDate >= new Date(startDate);

        // End date filter
        const matchesEndDate = !endDate || announcementDate <= new Date(endDate);

        return matchesText && matchesStartDate && matchesEndDate;
    });




    return (
        <Fragment>
            <div className="flex-1 pt-8 pb-8">
                <div className="container mx-auto px-4">
                    <div className="mb-10">


                        <div className="mb-3">
                            <Link
                                to="/admin"
                                className="inline-flex items-center text-primary-600 hover:text-primary-800 dark:text-primary-400 dark:hover:text-primary-300"
                            >
                                <ChevronLeftIcon className="w-5 h-5 me-2" />
                                {meta.backButton}
                            </Link>
                        </div>

                        {/* Title + Add Button Row */}
                        <div className="flex items-center justify-between">

                            {/* Title */}
                            <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100">
                                {meta.title}
                            </h1>

                            {/* Add Announcement Button */}
                            {canCreateAnnouncement() && <Button
                                onClick={handleCreateAnnouncementClick}
                                size="md"
                                className="flex items-center"
                            >
                                <PlusIcon className="w-5 h-5 me-2" />
                                {meta.createButton}
                            </Button>}

                        </div>
                    </div>

                    {loading && (
                        <div className="flex justify-center items-center min-h-[400px]">
                            <LoadingSpinner meta={LoadingSpinnerMeta} />
                        </div>
                    )}

                    {error && (
                        <div className="max-w-2xl mx-auto">
                            <div
                                className="bg-danger-100 dark:bg-danger-900 border border-danger-400 dark:border-danger-700 text-danger-700 dark:text-danger-200 px-6 py-4 rounded-lg">
                                <p className="font-bold text-lg mb-2">{meta.errorTitle}</p>
                                <p>{error}</p>
                            </div>
                        </div>
                    )}

                    {!loading && !error && announcements.length === 0 && (
                        <div className="text-center text-gray-500 dark:text-gray-400 py-12">
                            <p className="text-xl">{meta.noAnnouncementsMessage}</p>
                        </div>
                    )}

                    {!loading && !error && announcements.length > 0 && (
                        <>
                            <div className="mb-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 items-end w-full">

                                {/* Text Search */}
                                <div className="flex flex-col">
                                    <label className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                        Search by Title
                                    </label>
                                    <input
                                        type="text"
                                        placeholder={meta.announcementSearchPlaceHolder}
                                        value={filterText ?? ""}
                                        onChange={(e) => setFilterText(e.target.value)}
                                        className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg
                                    bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100
                                    focus:ring-2 focus:ring-primary-500 outline-none w-full max-w-md"
                                    />
                                </div>

                                {/* Start Date */}
                                <div className="flex flex-col w-full max-w-xs">

                                    <label className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                        Start Date
                                    </label>
                                    <input
                                        type="date"
                                        value={startDate ?? ""}
                                        onChange={(e) => setStartDate(e.target.value)}
                                        className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg
                                        bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100
                                        focus:ring-2 focus:ring-primary-500 outline-none"
                                    />
                                </div>

                                {/* End Date */}
                                <div className="flex flex-col w-full max-w-xs">

                                    <label className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                        End Date
                                    </label>
                                    <input
                                        type="date"
                                        value={endDate ?? ""}
                                        onChange={(e) => setEndDate(e.target.value)}
                                        className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg
                                        bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100
                                            focus:ring-2 focus:ring-primary-500 outline-none"
                                    />
                                </div>

                            </div>

                            {/* Announcements Table */}
                            <div className="overflow-x-auto rounded-lg">
                                <table className="w-full table-auto">
                                    <thead className="bg-gray-50 dark:bg-gray-600 text-start text-gray-600 dark:text-gray-300 uppercase text-sm font-semibold">
                                        <tr>
                                            <th className="px-6 py-3">Announcement</th>
                                            <th className="px-6 py-3">Publish Date</th>
                                            <th className="px-6 py-3">Priority</th>
                                            <th className="px-6 py-3 text-end">Actions</th>
                                        </tr>
                                    </thead>

                                    <tbody className="text-gray-800 dark:text-gray-200">
                                        {filterAnnouncements
                                            .slice((page - 1) * pageSize, page * pageSize)
                                            .map((announcement) => (
                                                <tr
                                                    key={announcement.id}
                                                    className="border-b border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700 transition hover:cursor-pointer"
                                                    onDoubleClick={() => handleEditAnnouncementClick(announcement)}
                                                >
                                                    {/* Title */}
                                                    <td className="px-6 py-4 font-medium">
                                                        {announcement.title}
                                                    </td>

                                                    {/* Publish Date */}
                                                    <td className="px-6 py-4">
                                                        {new Date(announcement.updated_at).toLocaleDateString()}
                                                    </td>

                                                    {/* Priority Boolean */}
                                                    <td className="px-6 py-4">
                                                        {announcement.priority ? (
                                                            <span className="text-primary-600 font-semibold">Yes</span>
                                                        ) : (
                                                            <span className="text-gray-500">No</span>
                                                        )}
                                                    </td>

                                                    {/* Actions */}
                                                    <td className="px-6 py-4">
                                                        <div className="flex justify-end gap-4">
                                                            {canEditAnnouncement(announcement) && <button
                                                                onClick={() => handleEditAnnouncementClick(announcement)}
                                                                className="text-primary-600 hover:text-primary-800"
                                                            >
                                                                <EyeIcon />
                                                            </button>}

                                                            {canDeleteAnnouncement(announcement) && <button
                                                                onClick={() => handleDeleteAnnouncementClick(announcement)}
                                                                className="text-danger-600 hover:text-danger-800"
                                                            >
                                                                <DeleteIcon></DeleteIcon>
                                                            </button>}
                                                        </div>
                                                    </td>
                                                </tr>
                                            ))}
                                    </tbody>
                                </table>
                            </div>

                            {/* Pagination */}
                            <div className="flex justify-between items-center mt-6">
                                <button
                                    disabled={page === 1}
                                    onClick={() => setPage((p) => p - 1)}
                                    className={`px-4 py-2 rounded-lg border ${page === 1
                                        ? "border-gray-300 text-gray-400 cursor-not-allowed"
                                        : "border-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700"
                                        }`}
                                >
                                    Previous
                                </button>

                                <span className="text-gray-600 dark:text-gray-300">
                                    Page {page} of {Math.ceil(filterAnnouncements.length / pageSize)}
                                </span>

                                <button
                                    disabled={page >= Math.ceil(filterAnnouncements.length / pageSize)}
                                    onClick={() => setPage((p) => p + 1)}
                                    className={`px-4 py-2 rounded-lg border ${page >= Math.ceil(filterAnnouncements.length / pageSize)
                                        ? "border-gray-300 text-gray-400 cursor-not-allowed"
                                        : "border-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700"
                                        }`}
                                >
                                    Next
                                </button>
                            </div>
                        </>
                    )}
                </div>
            </div>

            <AnnouncementFormModal
                meta={AnnouncementsFormModalMeta}
                isOpen={isAnnouncementModalOpen}
                onClose={handleClose}
                onSubmit={selectedAnnouncement ? handleUpdateAnnouncement : handleCreateAnnouncement}
                initialAnnouncement={selectedAnnouncement}
            />

            <ConfirmationModal
                meta={ConfirmationModalMeta}
                isOpen={isDeleteAnnouncementModalOpen}
                onCancel={handleClose}
                onConfirm={handleDeleteAnnouncementConfirm}
                title={meta.onDeleteTitle}
                message={meta.onDeleteMessage}
            />


        </Fragment>
    )
}
